import React, { useState, useEffect } from 'react';
import { X, Gift, Mail, Sparkles, Tag } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

const WelcomePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showExitOffer, setShowExitOffer] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const { currentTheme } = useTheme();

  useEffect(() => {
    const hasSignedUp = localStorage.getItem('welcomeSignup');
    const hasDismissed = sessionStorage.getItem('welcomeDismissed');
    
    if (!hasSignedUp && !hasDismissed) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isVisible) {
        handleDismiss();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isVisible]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    try {
      const discountCode = showExitOffer ? 'LOYALTY20' : 'WELCOME15';
      
      // Store signup info and discount code with email
      localStorage.setItem('welcomeSignup', 'true');
      localStorage.setItem('welcomeEmail', email);
      localStorage.setItem('welcomeName', name);
      localStorage.setItem('usedDiscount_' + email, discountCode);
      localStorage.setItem('discountCode', discountCode);
      
      // Show success toast
      toast({
        title: "🎉 Welcome Gift Unlocked!",
        description: `Your ${showExitOffer ? '20%' : '15%'} discount code: ${discountCode} - This is a one-time offer!`,
      });
      
      // Close popup immediately
      setIsVisible(false);
      setShowExitOffer(false);
      
      // Reset form
      setEmail('');
      setName('');
    } catch (error) {
      console.error('Error submitting welcome form:', error);
      // Still close the popup on error
      setIsVisible(false);
      setShowExitOffer(false);
    }
  };

  const handleDismiss = () => {
    if (!showExitOffer) {
      setShowExitOffer(true);
    } else {
      sessionStorage.setItem('welcomeDismissed', 'true');
      setIsVisible(false);
      setShowExitOffer(false);
    }
  };

  const handlePermanentClose = () => {
    sessionStorage.setItem('welcomeDismissed', 'true');
    localStorage.setItem('welcomeDeclined', 'true');
    setIsVisible(false);
    setShowExitOffer(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-[50]"
        style={{ animation: 'fadeIn 0.3s ease-out', pointerEvents: 'auto' }}
        onClick={handleDismiss}
      />

      <div className="fixed inset-0 z-[51] flex items-center justify-center p-4 pointer-events-none"
           style={{ animation: 'slideUp 0.4s ease-out' }}>
        
        {/* X Close Button - OUTSIDE popup, top right corner */}
        <button
          onClick={handleDismiss}
          type="button"
          className="fixed top-4 right-4 md:top-8 md:right-8 z-[999] w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-all hover:scale-110 active:scale-95 border-4 border-white shadow-2xl pointer-events-auto"
          style={{ 
            boxShadow: '0 0 30px rgba(255,0,0,0.8), 0 0 60px rgba(255,0,0,0.4)',
            animation: 'pulse 2s infinite'
          }}
          title={showExitOffer ? "Close" : "See better offer"}
        >
          <X className="w-8 h-8 text-white font-bold" strokeWidth={5} />
        </button>

        <div 
          className="relative max-w-lg w-full bg-zinc-950 rounded-3xl border-2 shadow-2xl max-h-[90vh] overflow-y-auto pointer-events-auto"
          style={{ borderColor: currentTheme.primary }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Permanent Close Button - Bottom Left */}
          <button
            onClick={handlePermanentClose}
            type="button"
            className="absolute bottom-4 left-4 z-[50] px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all font-bold border-2 border-white"
          >
            ✕ No thanks, close permanently
          </button>

          {showExitOffer ? (
            <ExitOfferContent 
              currentTheme={currentTheme}
              email={email}
              setEmail={setEmail}
              name={name}
              setName={setName}
              handleSubmit={handleSubmit}
            />
          ) : (
            <WelcomeOfferContent 
              currentTheme={currentTheme}
              email={email}
              setEmail={setEmail}
              name={name}
              setName={setName}
              handleSubmit={handleSubmit}
            />
          )}

          <div 
            className="h-1"
            style={{ 
              background: `linear-gradient(90deg, transparent, ${currentTheme.primary}, transparent)`,
              opacity: 0.5
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

const WelcomeOfferContent = ({ currentTheme, email, setEmail, name, setName, handleSubmit }) => (
  <>
    <div className="absolute inset-0 opacity-10">
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 30% 20%, ${currentTheme.primary}80, transparent 60%),
                      radial-gradient(circle at 70% 80%, ${currentTheme.primary}60, transparent 50%)`
        }}
      />
    </div>

    <div className="relative p-8 md:p-10">
      <div className="flex justify-center mb-6">
        <div 
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full"
          style={{ 
            background: `linear-gradient(135deg, ${currentTheme.primary}30, ${currentTheme.primary}10)`,
            border: `2px solid ${currentTheme.primary}`,
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}
        >
          <Sparkles className="w-4 h-4" style={{ color: currentTheme.primary }} />
          <span className="text-sm font-bold text-white uppercase tracking-wider">
            First Visit Exclusive
          </span>
        </div>
      </div>

      <div className="flex justify-center mb-6">
        <div 
          className="w-20 h-20 rounded-full flex items-center justify-center"
          style={{ 
            background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.primary}dd)`,
            boxShadow: `0 0 40px ${currentTheme.primary}60`,
            animation: 'bounce 1s infinite'
          }}
        >
          <Gift className="w-10 h-10 text-black" />
        </div>
      </div>

      <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4 uppercase">
        Welcome Gift
      </h2>

      <div className="mb-6 p-6 rounded-2xl text-center"
           style={{ 
             background: `linear-gradient(135deg, ${currentTheme.primary}20, ${currentTheme.primary}05)`,
             border: `1px solid ${currentTheme.primary}40`
           }}>
        <div className="flex items-center justify-center gap-3 mb-2">
          <Tag className="w-8 h-8" style={{ color: currentTheme.primary }} />
          <span className="text-6xl font-black" style={{ color: currentTheme.primary }}>
            15% OFF
          </span>
        </div>
        <p className="text-white font-bold text-xl">Your First Purchase</p>
      </div>

      <p className="text-gray-300 text-center mb-8 text-lg leading-relaxed">
        Join our VIP list and get instant access to your exclusive welcome discount. 
        <span className="block mt-2 font-bold text-white">Plus early access to new beats!</span>
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your Name"
          required
          className="w-full px-5 py-4 rounded-xl bg-black/50 border-2 border-white/10 text-white text-lg placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
        />

        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your Email Address"
          required
          className="w-full px-5 py-4 rounded-xl bg-black/50 border-2 border-white/10 text-white text-lg placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
        />

        <Button
          type="submit"
          className="w-full h-14 text-black font-bold text-lg hover:scale-105 transition-transform active:scale-95"
          style={{ backgroundColor: currentTheme.primary }}
        >
          <Mail className="w-5 h-5 mr-2" />
          Claim My 15% Discount
          <Sparkles className="w-5 h-5 ml-2" style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
        </Button>
      </form>

      <p className="text-gray-500 text-xs text-center mt-6 leading-relaxed">
        By signing up, you agree to receive exclusive offers and updates. 
        Unsubscribe anytime. Discount code: <span className="font-bold text-white">WELCOME15</span>
      </p>
    </div>
  </>
);

const ExitOfferContent = ({ currentTheme, email, setEmail, name, setName, handleSubmit }) => (
  <>
    <div className="absolute inset-0 opacity-10">
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 30% 20%, ${currentTheme.primary}80, transparent 60%),
                      radial-gradient(circle at 70% 80%, ${currentTheme.primary}60, transparent 50%)`
        }}
      />
    </div>

    <div className="relative p-8 md:p-10">
      <div className="flex justify-center mb-6">
        <div 
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full"
          style={{ 
            background: `linear-gradient(135deg, ${currentTheme.primary}30, ${currentTheme.primary}10)`,
            border: `2px solid ${currentTheme.primary}`,
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}
        >
          <Sparkles className="w-4 h-4" style={{ color: currentTheme.primary }} />
          <span className="text-sm font-bold text-white uppercase tracking-wider">
            Wait Up!
          </span>
        </div>
      </div>

      <div className="flex justify-center mb-6">
        <div 
          className="w-20 h-20 rounded-full flex items-center justify-center"
          style={{ 
            background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.primary}dd)`,
            boxShadow: `0 0 40px ${currentTheme.primary}60`
          }}
        >
          <Gift className="w-10 h-10 text-black" style={{ animation: 'bounce 1s infinite' }} />
        </div>
      </div>

      <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-6 leading-tight">
        Okay I Feel You Dawg!
      </h2>

      <p className="text-gray-300 text-center mb-6 text-lg leading-relaxed">
        Let me help ya out a lil bit! I know all bout the struggle, sometimes we can't do what it is we wanna do. 
      </p>

      <div className="mb-6 p-6 rounded-2xl text-center"
           style={{ 
             background: `linear-gradient(135deg, ${currentTheme.primary}20, ${currentTheme.primary}05)`,
             border: `1px solid ${currentTheme.primary}40`
           }}>
        <p className="text-white font-bold text-xl mb-3">
          So Imma let you get your <span className="underline decoration-2" style={{ textDecorationColor: currentTheme.primary }}>first beat</span> for
        </p>
        <div className="flex items-center justify-center gap-3 mb-2">
          <Tag className="w-10 h-10" style={{ color: currentTheme.primary }} />
          <span className="text-7xl font-black" style={{ color: currentTheme.primary }}>
            20% OFF
          </span>
        </div>
        <p className="text-white font-bold text-lg">Instead!</p>
      </div>

      <p className="text-gray-300 text-center mb-8 text-base">
        Real talk - this is the best deal you'll get. Don't sleep on it! 💯
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your Name"
          required
          className="w-full px-5 py-4 rounded-xl bg-black/50 border-2 border-white/10 text-white text-lg placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
        />

        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your Email Address"
          required
          className="w-full px-5 py-4 rounded-xl bg-black/50 border-2 border-white/10 text-white text-lg placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
        />

        <Button
          type="submit"
          className="w-full h-14 text-black font-bold text-lg hover:scale-105 transition-transform active:scale-95"
          style={{ backgroundColor: currentTheme.primary }}
        >
          <Mail className="w-5 h-5 mr-2" />
          Lock In My 20% Discount!
          <Sparkles className="w-5 h-5 ml-2" style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
        </Button>
      </form>

      <p className="text-gray-500 text-xs text-center mt-6 leading-relaxed">
        By signing up, you agree to receive exclusive offers and updates. 
        Unsubscribe anytime. Discount code: <span className="font-bold text-white">FIRSTBEAT20</span>
      </p>
    </div>
  </>
);

export default WelcomePopup;